export const ADD_PERSON_ACTION = 'Add Person';
export const ADD_RELATION_ACTION = 'Add Relation';
export const EDIT_PERSON_ACTION = 'Edit Person';

type MenuAction = typeof ADD_PERSON_ACTION | typeof ADD_RELATION_ACTION | typeof EDIT_PERSON_ACTION;

type CanvasContextMenuPermissions = {
  canAddPerson: boolean;
  canAddRelationship: boolean;
  canEditPerson: boolean;
};

export const getCanvasContextMenuActions = (
  targetPersonId: string | null,
  permissions: CanvasContextMenuPermissions
): MenuAction[] => {
  const actions: MenuAction[] = [];

  if (permissions.canAddPerson) {
    actions.push(ADD_PERSON_ACTION);
  }

  if (targetPersonId && permissions.canAddRelationship) {
    actions.push(ADD_RELATION_ACTION);
  }

  if (targetPersonId && permissions.canEditPerson) {
    actions.push(EDIT_PERSON_ACTION);
  }

  return actions;
};

type PositionedPerson = {
  id: string;
  x?: number;
  y?: number;
};

type CanvasPosition = {
  x: number;
  y: number;
};

export const buildContextAddPersonLayoutPositions = (
  people: PositionedPerson[],
  newPersonId: string,
  targetPosition: CanvasPosition
) => {
  return people.map((person) => ({
    personId: person.id,
    x: person.id === newPersonId ? targetPosition.x : (person.x ?? 500),
    y: person.id === newPersonId ? targetPosition.y : (person.y ?? 500),
  }));
};

export const buildRightClickRelationshipDraft = (personId: string) => ({
  personAId: personId,
  personBId: '',
});

export interface MenuKeyNavigationResult {
  preventDefault: boolean;
  nextIndex: number | null;
  closeMenu: boolean;
}

/**
 * Returns how the context menu should respond to a keyboard event.
 *
 * - ArrowDown: move focus to the next item (wraps)
 * - ArrowUp:   move focus to the previous item (wraps)
 * - Tab:       close the menu and let the browser handle focus naturally
 * - Other:     no-op
 *
 * @param key          The `event.key` string.
 * @param currentIndex The index of the currently focused menu item (-1 if none).
 * @param totalItems   Total number of visible menu items.
 */
export function evaluateMenuKeyNavigation(
  key: string,
  currentIndex: number,
  totalItems: number,
): MenuKeyNavigationResult {
  if (totalItems === 0) {
    return { preventDefault: false, nextIndex: null, closeMenu: key === 'Tab' };
  }

  if (key === 'ArrowDown') {
    return { preventDefault: true, nextIndex: (currentIndex + 1) % totalItems, closeMenu: false };
  }

  if (key === 'ArrowUp') {
    return { preventDefault: true, nextIndex: (currentIndex - 1 + totalItems) % totalItems, closeMenu: false };
  }

  if (key === 'Tab') {
    return { preventDefault: false, nextIndex: null, closeMenu: true };
  }

  return { preventDefault: false, nextIndex: null, closeMenu: false };
}

export type NodeTrayAction = typeof ADD_PERSON_ACTION | typeof ADD_RELATION_ACTION;

type NodeActionTrayPermissions = {
  canAddPerson: boolean;
  canAddRelationship: boolean;
};

/**
 * Returns the ordered list of action items to show in the node action tray
 * when a person node is selected on the canvas.
 *
 * The tray is the touch-friendly alternative to the right-click context menu;
 * it surfaces Add Person and Add Relation for devices where right-click is
 * unavailable. Desktop users can also use it as a secondary access path.
 */
export function getNodeActionTrayItems(
  permissions: NodeActionTrayPermissions,
): NodeTrayAction[] {
  const items: NodeTrayAction[] = [];
  if (permissions.canAddPerson) items.push(ADD_PERSON_ACTION);
  if (permissions.canAddRelationship) items.push(ADD_RELATION_ACTION);
  return items;
}
